import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PageHero } from '../components/PageHero';
import { Input } from '../components/ui/input';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { EXPORT_PAGES } from '../lib/pageContent';
import { EXPORT_HUB_PAGE } from '../constants/testIds';
import { CountryFlag } from '../components/CountryFlag';

const PUBLISHED_COUNTRIES = EXPORT_PAGES.filter((p) => p.type === 'country' && !p.draft);
const TOPIC_GUIDES = EXPORT_PAGES.filter((p) => p.type === 'topic' && !p.draft);

export default function ExportHubPage() {
  const [search, setSearch] = useState('');

  useDocumentMeta(
    'Export Destinations — Wholesale Used Tire Shipping Markets | BulkUsedTires.com',
    'Browse every country and port BulkUsedTires.com currently ships bulk used tire containers to, with compliance guidance, grades and shipping options for each market.',
    '/export'
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return PUBLISHED_COUNTRIES;
    return PUBLISHED_COUNTRIES.filter((p) =>
      [p.navLabel, p.country, p.port].filter(Boolean).some((v) => v.toLowerCase().includes(q))
    );
  }, [search]);

  return (
    <div className="min-h-screen bg-brand-bg">
      <Navbar />
      <PageHero
        eyebrow="Export Destinations"
        title="Where We Ship Bulk Used Tires"
        description="Every destination we currently export to — with the port, grades, shipping options and import-compliance guidance for that market. Search by country or port below."
      />

      <section data-testid={EXPORT_HUB_PAGE.section} className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 text-brand-muted absolute left-3.5 top-1/2 -translate-y-1/2" />
            <Input
              data-testid={EXPORT_HUB_PAGE.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by country or port…"
              className="pl-10 bg-brand-surface2 border-brand-border text-brand-text"
            />
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map((p) => (
              <Link
                key={p.slug}
                to={`/export/${p.slug}`}
                data-testid={EXPORT_HUB_PAGE.countryCard(p.slug)}
                className="rounded-2xl border border-brand-border bg-brand-surface p-6 hover:border-brand-orange transition-colors group"
              >
                <p className="flex items-center gap-2 font-display font-bold uppercase text-brand-text text-lg group-hover:text-brand-orange transition-colors">
                  <CountryFlag code={p.countryCode} className="w-5 h-3.5" />
                  {p.navLabel}
                </p>
                {p.port && <p className="mt-1 font-mono text-xs uppercase tracking-wider text-brand-amber">Port of {p.port}</p>}
                <p className="mt-3 text-brand-muted text-sm leading-relaxed line-clamp-2">{p.intro}</p>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <p data-testid={EXPORT_HUB_PAGE.noResults} className="mt-8 text-brand-muted text-sm">
              No destinations match "{search}" yet. <Link to="/#contact" className="text-brand-orange underline">Contact us</Link> to ask about a market not listed here.
            </p>
          )}

          {TOPIC_GUIDES.length > 0 && (
            <div className="mt-14">
              <h2 className="font-display font-bold uppercase text-brand-text text-lg mb-4">Export Guides &amp; Topics</h2>
              <div className="flex flex-wrap gap-3">
                {TOPIC_GUIDES.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/export/${p.slug}`}
                    className="font-mono text-xs uppercase tracking-wider text-brand-muted border border-brand-border rounded-full px-4 py-2 hover:border-brand-orange hover:text-brand-orange transition-colors"
                  >
                    {p.navLabel}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
